import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { FieldValues, UseFormReturn, FieldPath } from "react-hook-form";
import { ChangeEvent, useRef, useState } from "react";
import { Upload, X } from "lucide-react";

type FormFieldImagePropsType<T extends FieldValues> = {
  form: UseFormReturn<T, unknown, undefined>;
  label: string;
  name: FieldPath<T>;
  description?: string;
  /** Maksymalny rozmiar pliku w kilobajtach (liczony przed base64). Domyślnie 5120 KB (5 MB). */
  maxSizeKb?: number;
};

/**
 * Pole wgrywania obrazka (np. herb gminy). Plik jest konwertowany do base64
 * data URL i zapisywany bezpośrednio w form state — trafia do tenant.payload
 * jako string. Dla małych herbów (~20–100 KB) to jest OK. Dla większych
 * plików warto potem przejść na przechowywanie osobne + URL.
 */
function FormFieldImage<T extends FieldValues>({
  form,
  label,
  name,
  description,
  maxSizeKb = 5120,
}: FormFieldImagePropsType<T>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const value = (typeof field.value === "string" && field.value) || "";

        const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
          setError(null);
          const file = e.target.files?.[0];
          if (!file) return;
          if (!file.type.startsWith("image/")) {
            setError("Wybierz plik graficzny (PNG, JPG, SVG).");
            return;
          }
          if (file.size > maxSizeKb * 1024) {
            setError(`Plik za duży (${(file.size / 1024).toFixed(0)} KB). Maks. ${maxSizeKb} KB.`);
            return;
          }
          const reader = new FileReader();
          reader.onload = () => field.onChange(reader.result as string);
          reader.onerror = () => setError("Nie udało się odczytać pliku.");
          reader.readAsDataURL(file);
        };

        const handleClear = () => {
          field.onChange("");
          setError(null);
          if (inputRef.current) inputRef.current.value = "";
        };

        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <div className="flex items-start gap-4">
                {/* Preview */}
                <div className="w-20 h-20 rounded-md border border-gray-300 bg-white flex items-center justify-center overflow-hidden shrink-0">
                  {value ? (
                    <img src={value} alt="Podgląd" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-xs text-gray-400">brak</span>
                  )}
                </div>

                {/* Kontrolki */}
                <div className="flex flex-col gap-2 flex-1">
                  <label className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm cursor-pointer hover:bg-gray-50 hover:border-gray-400 w-fit">
                    <Upload className="h-4 w-4" />
                    {value ? "Zmień plik" : "Wybierz plik"}
                    <input
                      ref={inputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFile}
                      className="hidden"
                    />
                  </label>
                  {value && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline w-fit">
                      <X className="h-3 w-3" />
                      Usuń
                    </button>
                  )}
                  {error && <p className="text-xs text-red-600">{error}</p>}
                </div>
              </div>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export default FormFieldImage;
